import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { useApiClient } from '~/composables/api/api'
import { useQueryClient } from '@tanstack/vue-query'

export enum FavouriteType {
  none = 0,
  product = 1,
  riskAssessment = 2,
  document = 3,
  hazard = 4,
  processingAid = 5,
  location = 6,
}

// Helper to map FavouriteType to their ID key names
export const favouriteIdKeyMap = {
  [FavouriteType.product]: 'productId',
  [FavouriteType.riskAssessment]: 'riskAssessmentId',
  [FavouriteType.document]: 'documentId',
  [FavouriteType.hazard]: 'hazardId',
  [FavouriteType.processingAid]: 'processingAidId',
  [FavouriteType.location]: 'locationId',
}

export const favouriteStoreTypes = [
  FavouriteType.product,
  FavouriteType.riskAssessment,
  FavouriteType.document,
  FavouriteType.hazard,
  FavouriteType.processingAid,
  FavouriteType.location,
] as const

export type FavouriteResponse = {
  kindOf: FavouriteType
  id?: string | number
  [x: string]: number | string
}
export interface FavouriteStore {
  id: string | number
  object: FavouriteResponse
}

const normalizeFavouriteId = (id: string | number) => `${id}`
const isSameFavouriteId = (a: string | number, b: string | number) =>
  normalizeFavouriteId(a) === normalizeFavouriteId(b)
const createFavouriteKey = (id: string | number, type: FavouriteType) =>
  `${type}:${normalizeFavouriteId(id)}`

export const groupFavouritesByType = (values: FavouriteResponse[]) => {
  const grouped: Record<FavouriteType, FavouriteStore[]> = {
    [FavouriteType.product]: [],
    [FavouriteType.riskAssessment]: [],
    [FavouriteType.document]: [],
    [FavouriteType.hazard]: [],
    [FavouriteType.processingAid]: [],
    [FavouriteType.location]: [],
    [FavouriteType.none]: [],
  }

  for (const fav of values) {
    const idKey = favouriteIdKeyMap[fav.kindOf]
    if (!idKey) continue
    const id = fav[idKey] as string | number | undefined
    if (id === undefined) continue
    grouped[fav.kindOf].push({ id, object: fav })
  }

  return grouped
}

export const useFavouritesStore = (
  type: FavouriteType,
  onLoad?: (favourites: FavouriteStore[]) => void,
) => {
  return defineStore(`favourites-${type}`, () => {
    const authStore = useAuthStore()
    const favourites = ref<FavouriteStore[]>([])
    const fetchingFavourites = ref(false)
    const hasLoaded = ref(false)
    const loadingItems = ref<Set<string>>(new Set())

    const idKey = computed(() => {
      return favouriteIdKeyMap[type]
    })

    const updateFavourites = (_favourites: FavouriteStore[]) => {
      favourites.value = [..._favourites]
      hasLoaded.value = true
    }

    const hasFavourite = (id: string | number) => {
      return favourites.value.some((fav) => isSameFavouriteId(fav.id, id))
    }

    const findFavourite = (id: string | number) => {
      return favourites.value.find((fav) => isSameFavouriteId(fav.id, id))
    }

    const isItemLoading = (id: string | number) => {
      return loadingItems.value.has(createFavouriteKey(id, type))
    }

    const fetchFavourites = async () => {
      if (!authStore.canUseRoleProtectedPortalFeatures()) {
        fetchingFavourites.value = false
        updateFavourites([])
        return
      }

      try {
        fetchingFavourites.value = true
        const response = await useApiClient<{ values: FavouriteResponse[] }>('/portal/favourites', {
          method: 'GET',
          // Request a higher limit so local state accurately reflects server
          query: { kindOf: type, limit: 10000 },
        })
        fetchingFavourites.value = false
        const mapped = response.values.map((fav) => {
          const favouriteId = fav[idKey.value] as string | number
          return {
            id: favouriteId,
            object: fav,
          }
        })
        updateFavourites(mapped)
        if (response.values.length > 0 && onLoad) {
          onLoad(favourites.value)
        }
      } catch (error) {
        fetchingFavourites.value = false
        console.error('Failed to fetch favourites:', error)
        throw error
      }
    }

    return {
      favourites,
      updateFavourites,
      isItemLoading,
      hasFavourite,
      findFavourite,
      fetchFavourites,
      idKey,
      loadingItems,
      fetchingFavourites,
      hasLoaded,
    }
  })()
}
type TogglePayload = {
  id: string | number
  kindOf: FavouriteType
  favourite: boolean
}
export const useManageFavourite = (
  onMutate?: (payload: TogglePayload) => void,
  onToggled?: (payload: TogglePayload, data: FavouriteResponse) => void,
  onError?: (payload: TogglePayload) => void,
) => {
  const authStore = useAuthStore()
  const queryClient = useQueryClient()
  const toggleFavouriteMutation = useMutation({
    mutationFn: async ({
      id,
      kindOf,
      favourite,
    }: {
      id: string | number
      kindOf: FavouriteType
      favourite: boolean
    }) => {
      if (!authStore.canUseRoleProtectedPortalFeatures()) {
        throw new Error('Favourites are not available for users without application roles')
      }

      if (favourite) {
        return useApiClient<FavouriteResponse>(`/portal/favourites`, {
          method: 'POST',
          body: {
            [favouriteIdKeyMap[kindOf]]: id,
            kindOf,
          },
        })
      }

      return useApiClient<FavouriteResponse>(`/portal/favourites`, {
        method: 'DELETE',
        query: {
          kindOf,
          guid: kindOf === FavouriteType.document ? id : undefined,
          id: kindOf !== FavouriteType.document ? id : undefined,
        },
      })
    },

    onMutate: (payload) => {
      onMutate?.(payload)
    },

    onSettled: () => {
      return queryClient.invalidateQueries({ queryKey: ['portal', 'has-favourites'] })
    },
    onSuccess(data, variables, _context) {
      onToggled?.(variables, data)
    },
    onError(_error, variables) {
      onError?.(variables)
    },
  })

  return {
    toggleFavouriteMutation,
  }
}

export const useManageBulkFavourites = (
  onMutate?: (payload: { id: number; kindOf: FavouriteType }[]) => void,
  onToggled?: (payload: { id: number; kindOf: FavouriteType }[], data: FavouriteResponse[]) => void,
  onError?: (payload: { id: number; kindOf: FavouriteType }[]) => void,
  onSettled?: () => Promise<void>,
) => {
  const authStore = useAuthStore()
  const queryClient = useQueryClient()
  const bulkToggleFavouritesMutation = useMutation({
    mutationFn: async (requests: { id: number; kindOf: FavouriteType }[]) => {
      if (!authStore.canUseRoleProtectedPortalFeatures()) {
        throw new Error('Favourites are not available for users without application roles')
      }

      const payload = requests.map((request) => ({
        [favouriteIdKeyMap[request.kindOf]]: request.id,
        kindOf: request.kindOf,
      }))
      return await useApiClient<FavouriteResponse[]>(`/portal/favourites/batch`, {
        method: 'POST',
        body: payload,
      })
    },
    onMutate: (payload) => {
      onMutate?.(payload)
    },
    onSettled: async () => {
      await onSettled?.()
      return queryClient.invalidateQueries({ queryKey: ['portal', 'has-favourites'] })
    },
    onSuccess: (data, variables, _context) => {
      onToggled?.(variables, data)
    },
    onError: (_error, variables, _context) => {
      onError?.(variables)
    },
  })

  return {
    bulkToggleFavouritesMutation,
  }
}

export const useToggleFavourite = (kindOf: FavouriteType) => {
  const authStore = useAuthStore()
  const store = useFavouritesStore(kindOf)
  const loadingItems = store.loadingItems

  const isItemFavourited = (id: string | number) => {
    return store.hasFavourite(id)
  }

  const { toggleFavouriteMutation } = useManageFavourite(
    ({ id, kindOf }) => {
      const favouriteKey = createFavouriteKey(id, kindOf)
      if (loadingItems.has(favouriteKey)) return
      loadingItems.add(favouriteKey)
    },
    ({ favourite, id }, response) => {
      loadingItems.delete(createFavouriteKey(id, kindOf))
      if (favourite) {
        const newFavourite = {
          id: response[store.idKey] as string | number,
          object: response,
        }
        store.updateFavourites([...store.favourites, newFavourite])
      } else {
        store.updateFavourites(
          [...store.favourites].filter((item) => !isSameFavouriteId(item.id, id)),
        )
      }
    },
    ({ id, kindOf }) => {
      loadingItems.delete(createFavouriteKey(id, kindOf))
    },
  )
  const { isPending, mutate: toggleFavourite } = toggleFavouriteMutation
  const handleFavouriteToggling = (id: string | number, state?: boolean) => {
    if (!authStore.canUseRoleProtectedPortalFeatures()) return

    toggleFavourite({ favourite: !!state, id, kindOf })
  }

  const { bulkToggleFavouritesMutation } = useManageBulkFavourites(
    (payload) => {
      for (const request of payload) {
        const favouriteKey = createFavouriteKey(request.id as number, request.kindOf)
        if (loadingItems.has(favouriteKey)) return
        loadingItems.add(favouriteKey)
      }
    },
    (payload) => {
      for (const request of payload) {
        loadingItems.delete(createFavouriteKey(request.id as number, request.kindOf))
      }
    },
    (payload) => {
      for (const request of payload) {
        loadingItems.delete(createFavouriteKey(request.id as number, request.kindOf))
      }
    },
    async () => {
      await store.fetchFavourites()
    },
  )
  const { isPending: isBulkPending, mutate: bulkToggleFavourites } = bulkToggleFavouritesMutation

  const handleBulkFavouriteToggling = (requests: { id: number; kindOf: FavouriteType }[]) => {
    if (!authStore.canUseRoleProtectedPortalFeatures()) return

    bulkToggleFavourites(requests)
  }

  return {
    isItemFavourited,
    toggleFavourite,
    handleFavouriteToggling,
    isPending,
    isBulkPending,
    handleBulkFavouriteToggling,
  }
}
